import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import { FarmingResultsProduct } from '@/types/farmingResultsTypes';
import ImageModal from '@/components/pmt2/ImageModal';
import { useFarmingResultsSelection } from '@/context/FarmingResultsSelectionContext';
import { pmt2Service } from '@/services/pmt2Service';

interface FarmingProductHeaderProps {
  product: FarmingResultsProduct | null;
}

const FarmingProductHeader: React.FC<FarmingProductHeaderProps> = ({ product }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { selectedCompany } = useFarmingResultsSelection();
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  
  useEffect(() => {
    const loadSpreadsheetConfig = async () => {
      if (!selectedCompany) {
        setSpreadsheetId(null);
        return;
      }
      try {
        setConfigLoading(true);
        const configs = await pmt2Service.getSpreadsheetConfigs();
        const config = configs.find(c => c.companyName.toLowerCase() === selectedCompany.toLowerCase());
        setSpreadsheetId(config ? config.spreadsheetId : null);
      } catch (error) {
        console.error('Error loading spreadsheet config:', error);
        setSpreadsheetId(null);
      } finally {
        setConfigLoading(false);
      }
    };

    loadSpreadsheetConfig();
  }, [selectedCompany]);
  
  if (!product) return null;

  const getProductImages = (product: FarmingResultsProduct): string[] => {
    if (!product) return [];
    
    const images: string[] = [];
    
    for (let i = 1; i <= 8; i++) {
      const imageKey = `Image URL ${i}`;
      if (product[imageKey] && typeof product[imageKey] === 'string' && 
          product[imageKey] !== '--' && product[imageKey] !== '_' && 
          product[imageKey] !== '-' && !product[imageKey].startsWith('_')) {
        images.push(product[imageKey]);
      }
    }
    
    if (images.length === 0 && product['Image URL'] && 
        typeof product['Image URL'] === 'string' && 
        product['Image URL'] !== '--' && product['Image URL'] !== '_' && 
        product['Image URL'] !== '-' && !product['Image URL'].startsWith('_')) {
      images.push(product['Image URL']);
    }
    
    return images;
  };

  const images = getProductImages(product);
  const productTitle = product['Product Name'] || product.Title || product.id;
  
  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
  };
  
  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex === images.length - 1 ? 0 : prevIndex + 1));
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {images.length > 0 && (
          <div className="flex-shrink-0 mr-4">
            <img 
              src={images[0]} 
              alt={productTitle}
              className="h-16 w-16 object-contain cursor-pointer border border-gray-200 rounded"
              onClick={() => setIsModalOpen(true)}
              title="Click to enlarge"
            />
          </div>
        )}
        <div className="flex-grow">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{productTitle}</h2>
          <div className="mt-1 flex items-center flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">ASIN:</span>
            <a
              href={`https://www.amazon.com/dp/${product.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline"
            >
              {product.id} <ExternalLink className="h-3 w-3" />
            </a>
            {spreadsheetId && selectedCompany && !configLoading && (
              <>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Sheets:</span>
                <a
                  href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 dark:text-green-400 text-sm font-medium hover:underline flex items-center gap-1"
                  title={`Open ${selectedCompany} Spreadsheet`}
                >
                  {selectedCompany} <ExternalLink className="h-3 w-3" />
                </a>
              </>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 text-right">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</div>
          <div className="text-sm text-gray-900 dark:text-gray-100">
            {product.lastUpdated ? new Date(product.lastUpdated.toDate()).toLocaleString() : '-'}
          </div>
        </div>
      </div>

      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        images={images}
        currentIndex={currentImageIndex}
        onPrevious={handlePrevious}
        onNext={handleNext}
        alt={productTitle}
      />
    </div>
  );
};

export default FarmingProductHeader;